import {
  EventTypesEnum,
  LessonTypesEnum,
  PlaySessionMatchCompetitivenessEnum,
  SportsEnum,
} from 'types/generated/client';
import PickleRacket from 'svg/PickleRacket';
import Racket from 'svg/Racket';
import classNames from 'styles/utils/classNames';
import { ActivityBadgeProps, ActivityBadgeType, COURT_BOOKING_TYPE } from './types';

const TYPE_LABELS: Record<ActivityBadgeType, string> = {
  [LessonTypesEnum.Cardio]: 'Fitness',
  [LessonTypesEnum.Rfx]: 'Fitness',
  [LessonTypesEnum.Custom]: 'Fitness',
  [LessonTypesEnum.Clinic]: 'Clinic',
  [LessonTypesEnum.Camp]: 'Camp',
  [LessonTypesEnum.Individual]: 'Lesson',
  [LessonTypesEnum.Retreat]: 'Retreat',
  [EventTypesEnum.Tournament]: 'Tournament',
  [EventTypesEnum.League]: 'League',
  [EventTypesEnum.GeneralEvent]: 'Event',
  [EventTypesEnum.OpenPlay]: 'Open Play',
  [EventTypesEnum.Social]: 'Social',
  [COURT_BOOKING_TYPE]: 'Court booking',
};

const SPORT_LABELS: Partial<Record<SportsEnum, string>> = {
  [SportsEnum.Pickleball]: 'Pickleball',
  [SportsEnum.Tennis]: 'Tennis',
  [SportsEnum.Padel]: 'Padel',
  [SportsEnum.PlatformTennis]: 'Platform Tennis',
};

// Color tokens aligned with the design system (Figma "Badge / Activity type"):
//  Lesson    → lime         Fitness    → violet
//  Clinic    → brand-blue   Camp       → brand-green
//  OpenPlay  → brand-purple League     → brand-sea-blue
//  Tournament→ brand-yellow Social     → fuchsia
//  CourtBooking → teal (@todo design has no court-booking variant yet)
export const ACTIVITY_TYPE_COLORS: Record<ActivityBadgeType, string> = {
  [LessonTypesEnum.Individual]: 'bg-lime-100 text-lime-800',
  [LessonTypesEnum.Cardio]: 'bg-violet-100 text-violet-800',
  [LessonTypesEnum.Rfx]: 'bg-violet-100 text-violet-800',
  [LessonTypesEnum.Custom]: 'bg-violet-100 text-violet-800',
  [LessonTypesEnum.Retreat]: 'bg-violet-100 text-violet-800',
  [LessonTypesEnum.Clinic]: 'bg-brand-blue-100 text-brand-blue-800',
  [LessonTypesEnum.Camp]: 'bg-brand-green-100 text-brand-green-800',
  [EventTypesEnum.OpenPlay]: 'bg-brand-purple-100 text-brand-purple-800',
  [EventTypesEnum.GeneralEvent]: 'bg-brand-purple-100 text-brand-purple-800',
  [EventTypesEnum.League]: 'bg-brand-sea-blue-100 text-brand-sea-blue-800',
  [EventTypesEnum.Tournament]: 'bg-brand-yellow-100 text-brand-yellow-800',
  [EventTypesEnum.Social]: 'bg-fuchsia-100 text-fuchsia-800',
  [COURT_BOOKING_TYPE]: 'bg-teal-100 text-teal-800',
};

// A game's badge is coloured by how competitive it is, not by its activity type.
// @todo confirm with design: MATCH → "Competitive" is an assumption, and PRACTICE
// has no design yet (falls back to the activity-type colour).
const COMPETITIVENESS_LABELS: Record<PlaySessionMatchCompetitivenessEnum, string> = {
  [PlaySessionMatchCompetitivenessEnum.Casual]: 'Recreational',
  [PlaySessionMatchCompetitivenessEnum.Match]: 'Competitive',
  [PlaySessionMatchCompetitivenessEnum.Practice]: 'Practice',
};

const COMPETITIVENESS_COLORS: Partial<Record<PlaySessionMatchCompetitivenessEnum, string>> = {
  [PlaySessionMatchCompetitivenessEnum.Casual]: 'bg-brand-grass-100 text-brand-grass-800',
  [PlaySessionMatchCompetitivenessEnum.Match]: 'bg-brand-purple-100 text-brand-purple-800',
};

export default function ActivityBadge({
  type,
  sport,
  size = 'sm',
  sizeDesktop,
  className,
  competitiveness,
  isRfx = false,
}: ActivityBadgeProps) {
  const Icon =
    sport === SportsEnum.Tennis || sport === SportsEnum.Padel || sport === SportsEnum.PlatformTennis
      ? Racket
      : PickleRacket;

  const sportLabel = sport ? SPORT_LABELS[sport] : null;
  const typeLabel = competitiveness
    ? COMPETITIVENESS_LABELS[competitiveness]
    : isRfx
    ? 'RFX'
    : TYPE_LABELS[type];
  const label = sportLabel ? `${sportLabel} ${typeLabel}` : typeLabel;
  const colors =
    (competitiveness && COMPETITIVENESS_COLORS[competitiveness]) ??
    (isRfx ? ACTIVITY_TYPE_COLORS[LessonTypesEnum.Rfx] : ACTIVITY_TYPE_COLORS[type]) ??
    'bg-violet-100 text-violet-900';
  // Court bookings carry no sport, and the racket would assert one we don't know.
  const isShowIcon = type !== COURT_BOOKING_TYPE || !!sport;

  return (
    <div
      className={classNames(
        'flex w-fit items-center gap-1 rounded-full',
        size === 'xs' && 'typography-product-button-label-xs px-2 py-0.5',
        size === 'sm' && 'typography-product-button-label-small px-2.5 py-0.5',
        size === 'md' && 'typography-product-button-label-medium px-3 py-1',
        sizeDesktop === 'xs' && 'lg:typography-product-button-label-xs lg:px-2 lg:py-0.5',
        sizeDesktop === 'sm' && 'lg:typography-product-button-label-small lg:px-2.5 lg:py-0.5',
        sizeDesktop === 'md' && 'lg:typography-product-button-label-medium lg:px-3 lg:py-1',
        colors,
        className,
      )}
    >
      {isShowIcon && (
        <Icon
          className={classNames(
            size === 'xs' && 'h-3 w-3',
            size === 'sm' && 'h-3.5 w-3.5',
            size === 'md' && 'h-4 w-4',
            sizeDesktop === 'xs' && 'lg:h-3 lg:w-3',
            sizeDesktop === 'sm' && 'lg:h-3.5 lg:w-3.5',
            sizeDesktop === 'md' && 'lg:h-4 lg:w-4',
          )}
        />
      )}
      <span className="whitespace-nowrap">{label}</span>
    </div>
  );
}
